import { useEffect, useState } from "react";
import { mappingApi } from "@services/mappingApi";
import { mappingNotificationApi } from "@services/mappingNotificationApi";
import { applyMappingMessage } from "@utils/builders/mapping";
import { MappingViewStatus, type IMappingJobViewModel } from "@models/mapping";

const POLL_INTERVAL_MS = 5000;
// Give up waiting if no mapping job has appeared within this window. The job
// is created asynchronously after extraction publishes its SQS trigger, so a
// short gap is normal; but a lost message / mapping that never starts must not
// leave the UI polling (or showing "starting…") forever.
//
// The job is looked for by POLLING until it appears, socket or not: creating
// a job is not announced on the socket, and the first socket frame only comes
// once mapping has finished its first file — minutes after the job exists on
// a batch. Waiting for that frame (with a 90 s window) reported every slow
// job as "Mapping failed" until the page was reloaded. With polling, a job
// that exists is seen within one interval, so this window only has to cover
// a job that is genuinely never created.
const START_TIMEOUT_MS = 5 * 60_000;

interface MappingJobState {
  /** The mapping job once it exists, else null. */
  job: IMappingJobViewModel | null;
  /** True while we still expect a job (the async-create gap) or one is
   *  running. False once terminal, after the start-timeout gives up, or when
   *  disabled — so the UI can show "starting…" without spinning forever. */
  waiting: boolean;
}

interface RetryState {
  /** Bumped per retry so the watch effect restarts. */
  generation: number;
  /** The failed job being replaced; treated as "no job yet" while waiting. */
  staleJobId: string | null;
}

const isTerminal = (job: IMappingJobViewModel | null): boolean =>
  job?.status === MappingViewStatus.Completed ||
  job?.status === MappingViewStatus.Failed;

export const useMappingJob = (
  processId: string,
  enabled: boolean = true,
): MappingJobState => {
  const [job, setJob] = useState<IMappingJobViewModel | null>(null);
  const [waiting, setWaiting] = useState(Boolean(enabled && processId));
  const [retry, setRetry] = useState<RetryState>({ generation: 0, staleJobId: null });

  // A retry from any view of this process restarts the watch here as well.
  useEffect(() => {
    if (!processId) return;
    return mappingApi.onRetry((retriedProcessId, staleJobId) => {
      if (retriedProcessId !== processId) return;
      setRetry((prev) => ({ generation: prev.generation + 1, staleJobId }));
    });
  }, [processId]);

  useEffect(() => {
    if (!enabled || !processId) {
      setJob(null);
      setWaiting(false);
      return;
    }

    let cancelled = false;
    let pollTimer: number | null = null;
    let startTimeout: number | null = null;
    let unsubscribe: (() => void) | null = null;
    // Latest job seen, tracked outside React state so the start-timeout can
    // synchronously decide whether anything ever arrived.
    let current: IMappingJobViewModel | null = null;
    const staleJobId = retry.staleJobId;
    // Until mapping creates the retry job, the failed job is still the newest
    // one returned for the process; showing it would end the wait at once.
    const fresh = (vm: IMappingJobViewModel | null): IMappingJobViewModel | null =>
      vm && staleJobId && vm.jobId === staleJobId ? null : vm;

    setJob(null);
    setWaiting(true);

    // Whether the socket is delivering this process's frames. Once a job is
    // known, the socket carries it and the poll can stop.
    let socketLive = false;

    const stopPolling = (): void => {
      if (pollTimer !== null) {
        clearInterval(pollTimer);
        pollTimer = null;
      }
    };

    const stopAll = (): void => {
      stopPolling();
      if (startTimeout !== null) {
        clearTimeout(startTimeout);
        startTimeout = null;
      }
      unsubscribe?.();
      unsubscribe = null;
    };

    const settle = (vm: IMappingJobViewModel | null): void => {
      current = vm;
      if (cancelled) return;
      setJob(vm);
      if (isTerminal(vm)) {
        setWaiting(false);
        stopAll();
      } else if (vm && socketLive) {
        stopPolling();
      }
    };

    const fetchOnce = async (): Promise<IMappingJobViewModel | null> => {
      try {
        // 200 with a null body ("no job yet") is the normal async-create gap,
        // not an error — the API no longer 404s here.
        const vm = fresh(await mappingApi.getMappingJobByProcess(processId));
        settle(vm);
        return vm;
      } catch (error) {
        console.error(`Failed to fetch mapping job for process ${processId}:`, error);
        return null;
      }
    };

    const startPolling = (): void => {
      if (pollTimer !== null) return;
      pollTimer = window.setInterval(() => {
        void fetchOnce();
      }, POLL_INTERVAL_MS);
    };

    // Bounded wait: if no job ever shows up, stop watching and clear "waiting".
    startTimeout = window.setTimeout(() => {
      if (cancelled) return;
      if (current === null) {
        setWaiting(false);
        stopAll();
      }
    }, START_TIMEOUT_MS);

    void (async () => {
      const initial = await fetchOnce();
      if (cancelled || isTerminal(initial)) {
        return;
      }
      // Until the job exists, poll for it — the socket does not announce it
      // (see START_TIMEOUT_MS). The same covers a pending retry, whose new job
      // replaces a stale one.
      if (!initial || staleJobId) startPolling();
      try {
        await mappingNotificationApi.connect();
        if (cancelled) return;
        unsubscribe = mappingNotificationApi.subscribe(
          processId,
          (message) => {
            if (cancelled) return;
            if (staleJobId && message.job_id === staleJobId) return;
            const next = applyMappingMessage(current, message);
            current = next;
            setJob(next);
            if (!isTerminal(next)) {
              stopPolling(); // the socket now carries this job
            } else {
              setWaiting(false);
              stopAll();
              // The WebSocket terminal frame may omit failed_files. Fetch the
              // authoritative REST summary before settling the final count.
              void mappingApi.getMappingJobByProcess(processId)
                .then((snapshot) => {
                  if (!cancelled && snapshot && snapshot.jobId === next.jobId && isTerminal(snapshot)) {
                    settle(snapshot);
                  }
                })
                .catch((error) => {
                  console.error(`Failed to refresh mapping job for process ${processId}:`, error);
                });
            }
          },
        );
        socketLive = true;
        // A job the poll already found is the socket's from here on.
        if (current !== null) stopPolling();
      } catch (error) {
        console.warn(
          "Mapping notification socket unavailable, falling back to polling:",
          error,
        );
        if (!cancelled) {
          startPolling();
        }
      }
    })();

    return () => {
      cancelled = true;
      stopAll();
    };
  }, [processId, enabled, retry]);

  return { job, waiting };
};
